/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import dayjs from "dayjs";

import { TDictionary } from "../../../util/types";

import { SalesAgreement, SalesAgreementApiEntity, SalesAgreementProductLine } from "./types";

export const removeFreightItems = (x: SalesAgreementApiEntity) => {
  const linesNoFreight = x.lines.filter((line) => !line.isFreightItem);
  x.rawLines = [...linesNoFreight];

  const freightItem = x.lines.find((x) => x.isFreightItem);
  let freightChargeUnitPrice = 0;
  let freightChargeTotal = 0;
  if (freightItem) {
    freightChargeUnitPrice = freightItem?.unitPrice;
    freightChargeTotal = x.lines
      .filter((x) => x.isFreightItem)
      .reduce((acc, line) => acc + line.quantity.committed * freightChargeUnitPrice, 0);
  }

  x.freightChargeUnitPrice = freightChargeUnitPrice;
  x.freightChargeTotal = freightChargeTotal;
  x.productCostTotal = x.rawLines.reduce(
    (acc, line) => acc + line.quantity.committed * line.unitPrice,
    0
  );
  x.totalCost = x.productCostTotal + x.freightChargeTotal;
  x.lines = [...linesNoFreight];

  return x;
};

const shouldAddRemaining = (line: SalesAgreementProductLine) => {
  return line.scheduledDate && !dayjs(line.scheduledDate).isAfter(dayjs(), "month");
};

// Function to merge sale lines while keeping rawLines
export const mergeSaleLines = (data: SalesAgreementApiEntity[]) => {
  data.forEach((x) => {
    let temp: TDictionary<SalesAgreementProductLine> = {};
    x = removeFreightItems(x);

    x.lines.forEach((line) => {
      const lineKey = `${line.productVariantNumber}_${line.unitPrice}`;
      if (!Object.keys(temp).includes(lineKey) || temp[lineKey].unitPrice !== line.unitPrice) {
        temp = {
          ...temp,
          [lineKey]: {
            ...line,
            quantity: {
              ...line.quantity,
              monthRemaining: shouldAddRemaining(line) ? line.quantity.remaining : 0,
            },
          },
        };
      } else {
        const amendedLine = temp[lineKey];
        const currentMonthRemaining = amendedLine.quantity.monthRemaining ?? 0;

        amendedLine.quantity = {
          committed: amendedLine.quantity.committed + line.quantity.committed,
          delivered: amendedLine.quantity.delivered + line.quantity.delivered,
          invoiced: amendedLine.quantity.invoiced + line.quantity.invoiced,
          released: amendedLine.quantity.released + line.quantity.released,
          remaining: amendedLine.quantity.remaining + line.quantity.remaining,
          monthRemaining: shouldAddRemaining(line)
            ? currentMonthRemaining + line.quantity.remaining
            : currentMonthRemaining,
        };
        temp[lineKey] = amendedLine;
      }
    });

    x.lines = Object.keys(temp).map((key) => ({ ...temp[key] }));
  });

  return data;
};

// Function to convert entity to SalesAgreement while keeping rawLines
export const entityToSalesAgreement = (
  x: SalesAgreementApiEntity,
  accountNumber: string
): SalesAgreement => {
  const totalFulfilled = x.lines.reduce(
    (partialSum, saleLine) => partialSum + saleLine.quantity.delivered,
    0
  );
  const totalReleased = x.lines.reduce(
    (partialSum, saleLine) => partialSum + saleLine.quantity.released,
    0
  );
  const totalCommitted = x.lines.reduce(
    (partialSum, saleLine) => partialSum + saleLine.quantity.committed,
    0
  );

  const totalInvoiced = x.lines.reduce(
    (partialSum, saleLine) => partialSum + saleLine.quantity.invoiced,
    0
  );

  const avgI: number[] = [];
  x.lines.forEach((saleLine) => {
    avgI.push(
      (saleLine.quantity.committed - saleLine.quantity.remaining) / saleLine.quantity.committed
    );
  }, 0);

  const i = Math.floor((avgI.reduce((pv, v) => pv + v, 0) / avgI.length) * 100);

  const percentageFulfilled = Math.ceil(i);

  return {
    ...x,
    totalFulfilled,
    totalReleased,
    totalCommitted,
    totalInvoiced,
    percentageFulfilled,
    accountNumber,
  };
};

// Function to transform sales agreements while keeping rawLines
export const transformSalesAgreements = (
  data: SalesAgreementApiEntity[],
  accountNumber: string
): TDictionary<SalesAgreement> => {
  let dict: TDictionary<SalesAgreement> = {};
  data = mergeSaleLines(data);
  data.forEach((x) => {
    const agreement = entityToSalesAgreement(x, accountNumber);

    dict = {
      ...dict,
      [x.id]: agreement,
    };
  });

  return dict;
};
